/**
 * cloudBands — soft drifting cloud/nebula bands high in the SKY. A few LARGE,
 * very-soft, low-alpha `glow_soft` sprites scattered across the upper region
 * that drift slowly + horizontally (wrapping at the band edges) so the sky reads
 * as a living place rather than a flat backplane.
 *
 * DISTINCT from `fog`: fog is mid-screen haze; clouds are sky-anchored, much
 * larger, much slower, and sit BEHIND the parallax planes but in FRONT of the
 * celestial body (Z_LAYERS.CLOUDS) so they drift across the sun/moon. A constant
 * world fixture — mooded is false (combat heat should not pulse the sky).
 *
 * Same builder shape as the rest of the kit: narrow by kind, place from the
 * seed, mint soft-light sprites (createGlowSprite → null-safe behind IRenderer),
 * drive them with ONE shared ctx.addTick, return a sprite handle, self-register.
 * REDUCE-MOTION: the drift is the builder's own tick (not covered by the
 * AtmosphereLayer modulation freeze), so when reduced we place the clouds STATIC
 * and never attach the tick. The pref is read ONCE at build time.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { seededRand } from '../atmospherePlacement';
import { createGlowSprite } from '../../vfx/glowSpriteFactory';
import { vfxBudgetManager } from '../../VFXBudgetManager';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import type { ISprite } from '../../../rendering/IRenderer';
import { lerp } from '../../../../shared/utils/MathUtils';

const buildCloudBands: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'cloudBands') return null;
  const { region } = ctx;
  const spanY = region.y1 - region.y0;
  // One cloud-width of slack so a cloud is fully off-screen before it re-enters
  // (no pop at the edges). The wrap WINDOW is recomputed against the live camera
  // each frame in drift() — not pinned to the mount-time region.
  const margin = layer.sizeMax;

  // Read the reduce-motion pref ONCE: the drift is our own tick, NOT covered by
  // the AtmosphereLayer modulation freeze, so we must gate it here.
  const reduceMotion = getMotionPrefs().reduceMotion;

  const sprites: ISprite[] = [];
  // Each drifting cloud: a sprite + its current x + per-cloud drift velocity.
  const drifters: { s: ISprite; x: number; vx: number }[] = [];

  for (let i = 0; i < layer.count; i += 1) {
    if (!vfxBudgetManager.canSpawnShape()) break;
    const seed = ctx.seed + 600 + i * 17.3;
    const x = lerp(region.x0, region.x1, seededRand(seed));
    const y = lerp(
      region.y0 + spanY * layer.bandYMin,
      region.y0 + spanY * layer.bandYMax,
      seededRand(seed + 1),
    );
    const size = lerp(layer.sizeMin, layer.sizeMax, seededRand(seed + 2));
    const alpha = lerp(layer.alphaMin, layer.alphaMax, seededRand(seed + 3));
    const s = createGlowSprite({ x, y, size, color: layer.color, alpha, textureKey: layer.textureKey, blendMode: layer.blendMode, parent: ctx.layerContainer });
    if (!s) break; // textures not ready — quieter sky, never a crash
    sprites.push(s);
    if (!reduceMotion) {
      // Alternate drift direction per cloud so the band doesn't slide as a block.
      const dir = i % 2 === 0 ? 1 : -1;
      drifters.push({ s, x, vx: layer.driftPxPerSec * dir });
    }
  }

  if (!sprites.length) return null; // nothing placed — quieter scene, never a crash

  // Static (reduce-motion) → no tick, just the placed sprites.
  if (reduceMotion || !drifters.length) return makeContentHandle({ sprites });

  // One shared tick advances every cloud's x and wraps it within the live span.
  const drift = (dtMs: number) => {
    // Re-anchor the wrap window to the LIVE camera each frame (like critters /
    // parallax / skyGradient) so the cloud band stays over the player as the
    // camera pans, instead of being pinned to the mount-time world box. Fall
    // back to the region when there's no camera.
    const v = ctx.getCameraView();
    const left = (v ? v.centerX - v.width / 2 : region.x0) - margin;
    const span = (v ? v.width : region.x1 - region.x0) + margin * 2;
    const dt = dtMs / 1000;
    for (const d of drifters) {
      d.x += d.vx * dt;
      // Wrap horizontally so a cloud leaving one edge re-enters the other.
      if (d.x > left + span) d.x -= span;
      else if (d.x < left) d.x += span;
      d.s.setPosition(d.x, d.s.y);
    }
  };
  const unsub = ctx.addTick(drift);

  return makeContentHandle({ sprites, onDestroy: () => { unsub(); } });
};

registerEffect({ kind: 'cloudBands', build: buildCloudBands, depth: Z_LAYERS.CLOUDS, mooded: false });
